import React, { useEffect, useRef, useState } from "react";
import { ClipboardList, Trophy, Home, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const iconMap = {
  clipboard: ClipboardList,
  trophy: Trophy,
  home: Home,
};

const ConductSection = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const codes = t("conduct.codes") || [];

  return (
    <section id="conduct" ref={sectionRef} className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div
          className={`mb-12 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a2e] mb-4">
            {t("conduct.title")}
          </h2>
          <div className="w-20 h-1 bg-[#E8792B] rounded-full mb-6" />
          <p className="text-gray-600 text-base leading-relaxed max-w-4xl">
            {t("conduct.intro")}
          </p>
        </div>

        <p
          className={`text-gray-700 text-base leading-relaxed max-w-4xl mb-10 transition-all duration-700 delay-200 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {t("conduct.description")}
        </p>

        {/* GXR-style cards */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 mb-12">
          {Array.isArray(codes) &&
            codes.map((code, index) => {
              const Icon = iconMap[code.icon] || ClipboardList;
              const num = String(index + 1).padStart(2, "0");
              return (
                <div
                  key={index}
                  className={`group relative overflow-hidden p-8 lg:p-10 bg-white border border-gray-200 hover:border-[#E8792B]/50 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-500 ${
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                  }`}
                  style={{ transitionDelay: `${index * 120}ms` }}
                >
                  <div className="absolute -bottom-8 -right-2 font-black text-[160px] lg:text-[200px] leading-none text-[#E8792B]/[0.07] pointer-events-none select-none">
                    {num}
                  </div>

                  <span className="absolute top-0 left-0 h-px w-12 bg-[#E8792B] transition-all duration-500 group-hover:w-full" />

                  <div className="relative inline-flex items-center justify-center h-12 w-12 border border-[#E8792B]/40 bg-[#E8792B]/5 transition-all duration-300 group-hover:bg-[#E8792B]/10 group-hover:border-[#E8792B] group-hover:shadow-[0_0_24px_rgba(232,121,43,0.25)]">
                    <Icon className="h-5 w-5 text-[#E8792B]" strokeWidth={2} />
                  </div>

                  <div className="relative mt-8 text-[11px] font-bold tracking-[0.3em] text-[#E8792B]/70">
                    · {num} ·
                  </div>

                  <h3 className="relative mt-4 text-[22px] lg:text-[26px] font-bold leading-[1.2] text-[#1a1a2e] tracking-tight">
                    {code.title}
                  </h3>

                  <div className="relative mt-8 flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-transparent group-hover:text-[#E8792B] transition-all duration-300">
                    <span className="h-px w-6 bg-[#E8792B] transition-all duration-300 group-hover:w-10" />
                    <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </div>
              );
            })}
        </div>

        <div
          className={`bg-orange-50 border border-orange-200 rounded-xl p-6 transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <p className="text-gray-700 text-sm leading-relaxed">{t("conduct.violationNote")}</p>
        </div>
      </div>
    </section>
  );
};

export default ConductSection;
